import { Injectable } from '@angular/core';
import {
  HttpBackend,
  HttpClient,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Observable, catchError, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AUTH_CSRF, AUTH_ROUTES, CSRF_SAFE_METHODS } from './auth-session.constants';

/**
 * Sends the session cookie to the Cereus API and adds the CSRF header.
 *
 * Angular's built-in XSRF support only covers relative URLs. The portal calls
 * the API with absolute URLs (environment.apiUrl), so this interceptor:
 *  1. sets withCredentials on every request to the API / reporting API,
 *  2. adds X-XSRF-TOKEN (read from the XSRF-TOKEN cookie) to state-changing requests,
 *  3. fetches a token first (GET /auth/csrf) when the cookie is missing.
 * Requests to any other host are passed through untouched, so the token never leaks.
 */
@Injectable()
export class CredentialsInterceptor implements HttpInterceptor {
  private readonly apiOrigins: string[];
  /** Plain client without interceptors, used only to fetch the CSRF cookie. */
  private readonly rawHttp: HttpClient;
  /**
   * Latest token from the API's X-XSRF-TOKEN response header. Used first, because the
   * XSRF-TOKEN cookie is unreadable when the API is on another host without Csrf:CookieDomain.
   */
  private headerToken: string | null = null;

  constructor(httpBackend: HttpBackend) {
    this.rawHttp = new HttpClient(httpBackend);
    this.apiOrigins = [environment.apiUrl, environment.reportingUrl]
      .map(u => CredentialsInterceptor.originOf(u))
      .filter((o): o is string => !!o);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isApiRequest(req.url)) {
      return next.handle(req);
    }

    const withCookies = req.clone({ withCredentials: true });
    if (CSRF_SAFE_METHODS.includes(req.method.toUpperCase())) {
      return next.handle(withCookies).pipe(tap({ next: e => this.remember(e), error: e => this.remember(e) }));
    }

    const token = this.currentToken();
    const first$ = token
      ? next.handle(this.addToken(withCookies, token))
      // No token yet (first visit, cookie expired, or cleared): get one, then send.
      : this.refreshToken().pipe(switchMap(() => this.sendWithCurrentToken(withCookies, next)));

    // The API answers 400 + X-CSRF-Failed when the token is stale (e.g. the session expired and
    // the token was bound to the old user). Get a fresh token and retry once.
    return first$.pipe(
      tap({ next: e => this.remember(e), error: e => this.remember(e) }),
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 400 && err.headers?.get('X-CSRF-Failed')) {
          this.headerToken = null;   // never reuse the rejected token
          return this.refreshToken().pipe(switchMap(() => this.sendWithCurrentToken(withCookies, next)));
        }
        return throwError(() => err);
      })
    );
  }

  private refreshToken(): Observable<unknown> {
    return this.rawHttp
      .get(`${environment.apiUrl}${AUTH_ROUTES.csrf}`, { withCredentials: true, observe: 'response' })
      .pipe(tap(r => this.remember(r)), catchError(() => of(null)));
  }

  /**
   * Only the token the API sent in the X-XSRF-TOKEN response header. The XSRF-TOKEN cookie is NOT used:
   * an old copy on the parent domain (.nucleusip.com, set by an earlier build or another environment's
   * API) is readable here and can't be decrypted by this API ("The antiforgery token could not be decrypted").
   * With no header token yet, the request first fetches one from /auth/csrf.
   */
  private currentToken(): string | null {
    return this.headerToken;
  }

  /** Keeps the token the API sends back in the X-XSRF-TOKEN response header (login, me, logout, csrf). */
  private remember(event: unknown): void {
    const headers = event instanceof HttpResponse || event instanceof HttpErrorResponse ? event.headers : null;
    const token = headers?.get(AUTH_CSRF.headerName);
    if (token) {
      this.headerToken = token;
    }
  }

  private sendWithCurrentToken(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const fresh = this.currentToken();
    // Replace any stale header from the first attempt.
    const clean = req.headers.has(AUTH_CSRF.headerName) ? req.clone({ headers: req.headers.delete(AUTH_CSRF.headerName) }) : req;
    return next.handle(fresh ? this.addToken(clean, fresh) : clean);
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    if (req.headers.has(AUTH_CSRF.headerName)) {
      return req;
    }
    return req.clone({ setHeaders: { [AUTH_CSRF.headerName]: token } });
  }

  private isApiRequest(url: string): boolean {
    const origin = CredentialsInterceptor.originOf(url);
    if (!origin) {
      return false;
    }
    // Relative URLs (proxy mode) resolve to the page origin.
    return this.apiOrigins.includes(origin) || (origin === window.location.origin && !/^https?:/i.test(url));
  }

  private static originOf(url: string | undefined): string | null {
    if (!url || url.includes('#{')) {
      return null; // un-replaced deployment token
    }
    try {
      return new URL(url, window.location.origin).origin;
    } catch {
      return null;
    }
  }
}
